import { Component, ViewChild, OnInit, effect } from '@angular/core';

import { SupervisorBaseComponent } from '../supervisor-base.component';
import { SupervisorHelperService } from '../supervisor.helper.service';
import { AuthService } from '../../auth/auth.service';
import { Router, ActivatedRoute } from '@angular/router';
import { AlertService } from '../../shared/services/alert.service';
import { StaffApiService, SupervisorApiService } from '../../core/api/services';
import { ApexPlotOptions, ChartComponent } from 'ng-apexcharts';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexYAxis,
  ApexTitleSubtitle,
  ApexDataLabels,
  ApexGrid,
  ApexStroke,
  ApexTooltip,
  ApexFill,
  ApexStates,
  ApexLegend,
} from 'ng-apexcharts';
import {
  PatientOverviewDataModel,
  OverviewStatistics,
  OverviewTarget,
  OverviewCurrentTarget,
  OverviewTrialCount,
  OverviewSeries,
  PatientTargetModel,
} from '../../core/api/models';
import { Utils } from '../../core/utils';
import { Target } from '@angular/compiler';
import { PatientDataFacadeService } from '../../core/services/patient-data-facade.service';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  title: ApexTitleSubtitle;
  colors: string[];
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  stroke: ApexStroke;
  tooltip?: ApexTooltip;
  plotOptions?: ApexPlotOptions;
  fill?: ApexFill;
  states?: ApexStates;
  legend?: ApexLegend;
};

// interface Student {
//   id: number;
//   name: string;
// }

// interface Target {
//   id: number;
//   name: string;
//   dateOpened: Date;
//   dateMastered?: Date;
//   dateFailed?: Date;
//   trialsToMastery?: number;
//   teachingDaysToMastery?: number;
//   trialCount?: number;
//   percentage?: number;
// }

// interface TrialCount {
//   name: string;
//   count: number;
// }

// interface CurrentTarget {
//   skillArea: string;
//   program: string;
//   targets: string;
// }

// interface Statistics {
//   totalTargets: number;
//   masterTargets: number;
//   maintenanceTargets: number;
//   percentageMastered: number;
//   avgTrialsToMastery: number;
//   avgTeachingDaysToMastery: number;
//   openTargets: number;
//   percentageFailedMaintenance: number;
//   percentageTrendingUp: number;
// }

export type OverviewDatePreset =
  | 'last_week'
  | 'last_15_days'
  | 'last_month'
  | 'last_3_months'
  | 'last_6_months'
  | 'custom';

@Component({
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.css'],
  standalone: false,
})
export class OverviewComponent
  extends SupervisorBaseComponent
  implements OnInit
{
  @ViewChild('chart') chart!: ChartComponent;

  targetToOpen: PatientTargetModel | null = null;

  readonly datePresetOptions: { value: OverviewDatePreset; label: string }[] = [
    { value: 'last_week', label: 'Last week' },
    { value: 'last_15_days', label: 'Last 15 days' },
    { value: 'last_month', label: 'Last month' },
    { value: 'last_3_months', label: 'Last three months' },
    { value: 'last_6_months', label: 'Last six months' },
    { value: 'custom', label: 'Custom' },
  ];
  datePreset: OverviewDatePreset = 'last_3_months';
  filterStartDate!: Date;
  filterEndDate!: Date;

  /** Period label shown in card titles; updated only after data is successfully loaded for that period. */
  loadedPeriodLabel = 'Last three months';

  statistics: OverviewStatistics = {};

  // Data for tables

  highestPerformingTargets: OverviewTarget[] = [];

  currentTargetsInBaseline: OverviewCurrentTarget[] = [];

  trialCountsToday: OverviewTrialCount[] = [];

  averageTrialCounts: OverviewTrialCount[] = [];

  targetsTrendingUp: OverviewTarget[] = [];

  targetsTrendingFlat: OverviewTarget[] = [];

  recentlyMasteredTargets: OverviewTarget[] = [];

  lowestPerformingTargets: OverviewTarget[] = [];

  // Chart options
  public averageTrialChartOptions: Partial<ChartOptions> = {
    series: [
      {
        name: 'Average Trials',
        data: [1.75],
      },
    ],
    chart: {
      height: 250,
      type: 'bar',
      toolbar: {
        show: false,
      },
    },
    colors: ['#68a1b7'],
    xaxis: {
      categories: ['Task Analysis: Washing Hands'],
    },
    yaxis: {
      title: {
        text: 'Average Trial Count',
      },
    },
    dataLabels: {
      enabled: true,
    },
    grid: {
      row: {
        colors: ['#f3f3f3', 'transparent'],
        opacity: 0.5,
      },
    },
    stroke: {
      width: 2,
    },
    title: {
      text: '',
      align: 'center',
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return val.toFixed(2);
        },
      },
    },
  };

  public targetsMasteredChartOptions: Partial<ChartOptions> = {
    series: [
      {
        name: 'Targets Mastered',
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
          0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1,
          2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
          2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
        ],
      },
    ],
    chart: {
      height: 250,
      type: 'line',
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    colors: ['#8ac379'],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'straight',
      width: 3,
    },
    grid: {
      row: {
        colors: ['#f3f3f3', 'transparent'],
        opacity: 0.5,
      },
    },
    xaxis: {
      categories: [
        'Mar 12',
        'Mar 13',
        'Mar 14',
        'Mar 15',
        'Mar 16',
        'Mar 17',
        'Mar 18',
        'Mar 19',
        'Mar 20',
        'Mar 21',
        'Mar 22',
        'Mar 23',
        'Mar 24',
        'Mar 25',
        'Mar 26',
        'Mar 27',
        'Mar 28',
        'Mar 29',
        'Mar 30',
        'Mar 31',
        'Apr 1',
        'Apr 2',
        'Apr 3',
        'Apr 4',
        'Apr 5',
        'Apr 6',
        'Apr 7',
        'Apr 8',
        'Apr 9',
        'Apr 10',
        'Apr 11',
        'Apr 12',
        'Apr 13',
        'Apr 14',
        'Apr 15',
        'Apr 16',
        'Apr 17',
        'Apr 18',
        'Apr 19',
        'Apr 20',
        'Apr 21',
        'Apr 22',
        'Apr 23',
        'Apr 24',
        'Apr 25',
        'Apr 26',
        'Apr 27',
        'Apr 28',
        'Apr 29',
        'Apr 30',
        'May 1',
        'May 2',
        'May 3',
        'May 4',
        'May 5',
        'May 6',
        'May 7',
        'May 8',
        'May 9',
        'May 10',
        'May 11',
        'May 12',
      ],
      labels: {
        rotate: -45,
        rotateAlways: false,
        hideOverlappingLabels: true,
        maxHeight: 100,
      },
    },
    yaxis: {
      title: {
        text: 'Targets Mastered',
      },
      min: 0,
      tickAmount: 4,
    },
    title: {
      text: '',
      align: 'center',
    },
  };

  public targetsStatusChartOptions: Partial<ChartOptions> = {
    series: [
      { name: 'Waiting or Unknown', data: [1] },
      { name: 'Acquisition', data: [2] },
      { name: 'Mastered', data: [3] },
      { name: 'Maintenance', data: [4] },
      { name: 'Closed', data: [5] },
    ],
    chart: {
      type: 'bar',
      height: 50,
      width: 500,
      stacked: true,
      dropShadow: {
        enabled: true,
        blur: 1,
        opacity: 0.25,
      },
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    grid: {
      padding: {
        left: 0,
        right: 0,
        top: -20,
        bottom: -20,
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '90%',
        //distributed: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 2,
    },
    title: {
      text: undefined,
    },
    xaxis: {
      categories: ['Targets Status'],
      labels: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      min: 0,
      max: 15,
    },
    yaxis: {
      title: {
        text: undefined,
      },
      labels: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    tooltip: {
      shared: false,
      y: {
        formatter: function (val) {
          return val + ' targets';
        },
      },
    },
    colors: ['#DFE2AF', '#8AC476', '#68A1B7', '#CC9E7F', '#B2B2B2'],
    fill: {
      type: 'solid',
      opacity: 1,
      pattern: {
        style: [
          'circles',
          'slantedLines',
          'verticalLines',
          'horizontalLines',
          'squares',
        ],
        strokeWidth: 2,
      },
    },
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
    },
    legend: {
      show: false,
      position: 'bottom',
    },
  };

  programsStatusChartSeries: any[] = [
    { name: 'Waiting or Unknown', data: [1, 2, 3, 4, 5] },
    { name: 'Acquisition', data: [2, 2, 3, 4, 5] },
    { name: 'Mastered', data: [3, 2, 3, 4, 5] },
    { name: 'Maintenance', data: [4, 2, 3, 4, 5] },
    { name: 'Closed', data: [5, 2, 3, 4, 5] },
  ];

  public programsStatusChartOptions: Partial<ChartOptions> = {
    series: this.programsStatusChartSeries,
    chart: {
      type: 'bar',
      height: 250,
      width: '98%',
      stacked: true,
      dropShadow: {
        enabled: true,
        blur: 1,
        opacity: 0.25,
      },
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    grid: {
      padding: {
        left: 15,
        right: 5,
        top: 5,
        bottom: 5,
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '80%',
        //distributed: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 2,
    },
    title: {
      text: undefined,
    },
    xaxis: {
      categories: [
        'Program 1',
        'Program 2',
        'Program 3',
        'Program 4',
        'Program 5',
      ],
      labels: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      max: 20,
    },
    yaxis: {
      title: {
        text: undefined,
      },
      labels: {
        show: true,
      },
      axisBorder: {
        show: true,
      },
      axisTicks: {
        show: false,
      },
    },
    tooltip: {
      shared: false,
      y: {
        formatter: function (val) {
          return val + ' targets';
        },
      },
    },
    colors: ['#DFE2AF', '#8AC476', '#68A1B7', '#CC9E7F', '#B2B2B2'],
    fill: {
      type: 'pattern',
      opacity: 1,
      pattern: {
        //style: ["circles", "slantedLines", "verticalLines", "horizontalLines", "squares"],
        style: 'squares',
        strokeWidth: 2,
      },
    },
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
    },
    legend: {
      show: true,
      position: 'top',
    },
  };

  patientID: number = 0;

  constructor(
    protected override supervisorApiService: SupervisorApiService,
    protected override alertService: AlertService,
    protected override supervisorHelperService: SupervisorHelperService,
    protected override authService: AuthService,
    protected override staffApiService: StaffApiService,
    private router: Router,
    private route: ActivatedRoute,
    private patientDataFacadeService: PatientDataFacadeService
  ) {
    super(
      supervisorApiService,
      alertService,
      supervisorHelperService,
      authService,
      staffApiService,
      patientDataFacadeService
    );

    effect(() => {
      if (!this.supervisorHelperService.showAddTarget()) {
        if (this.targetToOpen) {
          this.isLoading = true;
          this.patientDataFacadeService
            .getTargets(this.patientID, {
              onlyActive: false,
            })
            .subscribe({
              next: (targets: PatientTargetModel[]) => {
                this.supervisorHelperService.targets.set(targets);
                this.targetToOpen =
                  targets.find((t) => t.ID === this.targetToOpen?.ID) ?? null;
                this.isLoading = false;
              },
              error: (error: any) => {
                this.isLoading = false;
              },
            });
        }
      }
    });
  }

  ngOnInit(): void {
    this.applyPresetToDates();
    this.route.params.subscribe((params: any) => {
      this.patientID = params.patientID;
      this.loadData(params.patientID);
    });
  }

  getDefaultStartDate(): Date {
    return this.getStartDateForPreset('last_3_months');
  }

  getDefaultEndDate(): Date {
    return this.getEndDateForPreset();
  }

  getStartDateForPreset(preset?: OverviewDatePreset): Date {
    const p = preset ?? this.datePreset;
    if (p === 'custom') {
      return this.filterStartDate ?? new Date();
    }
    const end = this.getEndDateForPreset(p);
    const d = new Date(end);
    switch (p) {
      case 'last_week':
        d.setDate(d.getDate() - 7);
        break;
      case 'last_15_days':
        d.setDate(d.getDate() - 15);
        break;
      case 'last_month':
        d.setMonth(d.getMonth() - 1);
        break;
      case 'last_3_months':
        d.setMonth(d.getMonth() - 3);
        break;
      case 'last_6_months':
        d.setMonth(d.getMonth() - 6);
        break;
      default:
        d.setMonth(d.getMonth() - 3);
    }
    return d;
  }

  getEndDateForPreset(preset?: OverviewDatePreset): Date {
    const p = preset ?? this.datePreset;
    if (p === 'custom' && this.filterEndDate) {
      return this.filterEndDate;
    }
    return new Date();
  }

  applyPresetToDates(): void {
    if (this.datePreset === 'custom') {
      if (!this.filterStartDate)
        this.filterStartDate = this.getStartDateForPreset('last_3_months');
      if (!this.filterEndDate)
        this.filterEndDate = this.getEndDateForPreset('last_3_months');
      return;
    }
    this.filterEndDate = this.getEndDateForPreset(this.datePreset);
    this.filterStartDate = this.getStartDateForPreset(this.datePreset);
  }

  onPresetChange(): void {
    this.applyPresetToDates();
    this.refresh();
  }

  onFilterDateChange(): void {
    this.datePreset = 'custom';
  }

  onStartDateChange(value: string): void {
    if (value) {
      this.filterStartDate = Utils.parseDateFromInput(value);
      this.onFilterDateChange();
      this.refresh();
    }
  }

  onEndDateChange(value: string): void {
    if (value) {
      this.filterEndDate = Utils.parseDateFromInput(value);
      this.onFilterDateChange();
      this.refresh();
    }
  }

  get periodLabel(): string {
    if (this.datePreset === 'custom') {
      if (!this.filterStartDate || !this.filterEndDate) return 'Custom';
      const fmt = (d: Date) =>
        d.toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      return `${fmt(this.filterStartDate)} – ${fmt(this.filterEndDate)}`;
    }
    const opt = this.datePresetOptions.find((o) => o.value === this.datePreset);
    return opt?.label ?? 'Last three months';
  }

  refresh(): void {
    this.loadData(this.patientID);
  }

  loadData(patientID: number): void {
    if (patientID == 0) {
      return;
    }
    if (this.filterStartDate != null && this.filterEndDate != null) {
      const startDay = new Date(
        this.filterStartDate.getFullYear(),
        this.filterStartDate.getMonth(),
        this.filterStartDate.getDate()
      ).getTime();
      const endDay = new Date(
        this.filterEndDate.getFullYear(),
        this.filterEndDate.getMonth(),
        this.filterEndDate.getDate()
      ).getTime();
      if (endDay < startDay) {
        this.alertService.error(
          'End date must be on or after the start date. Please select a valid date range.'
        );
        return;
      }
    }
    const startStr = this.filterStartDate
      ? Utils.formatDateForInput(this.filterStartDate)
      : undefined;
    const endStr = this.filterEndDate
      ? Utils.formatDateForInput(this.filterEndDate)
      : undefined;
    this.isLoading = true;
    this.supervisorApiService
      .GetPatientOverviewData({
        patientID,
        startDate: startStr,
        endDate: endStr,
      })
      .subscribe({
        next: (data: PatientOverviewDataModel) => {
          this.refreshAllData(data);
          this.loadedPeriodLabel = this.periodLabel;
          this.isLoading = false;
        },
        error: (error: any) => {
          this.isLoading = false;
        },
      });
  }

  refreshAllData(data: PatientOverviewDataModel): void {
    this.statistics = data.Statistics ?? {};
    this.highestPerformingTargets = data.HighestPerformingTargets ?? [];
    this.currentTargetsInBaseline = data.CurrentTargetsInBaseline ?? [];
    this.trialCountsToday = data.TrialCountsToday ?? [];
    this.averageTrialCounts = data.AverageTrialCounts ?? [];
    this.targetsTrendingUp = data.TargetsTrendingUp ?? [];
    this.targetsTrendingFlat = data.TargetsTrendingFlat ?? [];
    this.recentlyMasteredTargets = data.RecentlyMasteredTargets ?? [];
    this.lowestPerformingTargets = data.LowestPerformingTargets ?? [];

    this.programsStatusChartSeries =
      data.ProgramsStatusChartData?.Series?.map((series: OverviewSeries) => ({
        name: series.Name,
        data: series.Data,
      })) ?? [];
    this.programsStatusChartOptions.series = this.programsStatusChartSeries;
    if (!this.programsStatusChartOptions.xaxis) {
      this.programsStatusChartOptions.xaxis = {};
    }
    // Calculate max value for xaxis by summing values at each index across all series
    if (this.programsStatusChartSeries.length > 0) {
      const dataLength = this.programsStatusChartSeries[0].data.length;
      const columnSums = [];

      // For each index position
      for (let i = 0; i < dataLength; i++) {
        // Sum the values across all series at this index
        const sum = this.programsStatusChartSeries.reduce((total, series) => {
          return total + (series.data[i] || 0);
        }, 0);
        columnSums.push(sum);
      }

      // Set max to the highest column sum
      this.programsStatusChartOptions.xaxis.max = Math.max(...columnSums);
    } else {
      this.programsStatusChartOptions.xaxis.max = 0;
    }

    this.programsStatusChartOptions.xaxis.min = 0;
    this.programsStatusChartOptions.xaxis.categories =
      data.ProgramsStatusChartData?.Categories ?? [];
    this.programsStatusChartOptions.colors =
      data.ProgramsStatusChartData?.Colors ?? [];

    if (this.programsStatusChartSeries.length > 0) {
      //make the chart height dynamic based on the number of series
      this.programsStatusChartOptions.chart!.height =
        this.programsStatusChartSeries[0].data.length * 35;
    }

    let targetsMasteredSeries: any[] =
      data.TargetsMasteredChartData?.Series?.map((series: OverviewSeries) => ({
        name: series.Name,
        data: series.Data,
      })) ?? [];
    this.targetsMasteredChartOptions.series = targetsMasteredSeries;
    if (!this.targetsMasteredChartOptions.xaxis) {
      this.targetsMasteredChartOptions.xaxis = {};
    }
    this.targetsMasteredChartOptions.xaxis.tickAmount =
      (data.TargetsMasteredChartData?.Categories?.length ?? 0) / 10;
    this.targetsMasteredChartOptions.xaxis.categories =
      data.TargetsMasteredChartData?.Categories ?? [];
    this.targetsMasteredChartOptions.colors =
      data.TargetsMasteredChartData?.Colors ?? [];

    let targetsStatusSeries: any[] =
      data.TargetsStatusChartData?.Series?.map((series: OverviewSeries) => ({
        name: series.Name,
        data: series.Data,
      })) ?? [];
    this.targetsStatusChartOptions.series = targetsStatusSeries;
    if (!this.targetsStatusChartOptions.xaxis) {
      this.targetsStatusChartOptions.xaxis = {};
    }
    this.targetsStatusChartOptions.xaxis.max = targetsStatusSeries.reduce(
      (sum: number, series: any) => {
        const seriesSum = series.data.reduce(
          (seriesSum: number, value: number) => seriesSum + value,
          0
        );
        return sum + seriesSum;
      },
      0
    );
    this.targetsStatusChartOptions.xaxis.categories =
      data.TargetsStatusChartData?.Categories ?? [];
    this.targetsStatusChartOptions.colors =
      data.TargetsStatusChartData?.Colors ?? [];
  }

  openTarget(target: { Id: number }): void {
    if (
      !this.supervisorHelperService.targets().length ||
      !this.supervisorHelperService.targets().find((t) => t.ID === target.Id)
    ) {
      this.isLoading = true;
      const getTargetsObservable = this.patientDataFacadeService.getTargets(
        this.patientID,
        {
          onlyActive: false,
        }
      );
      getTargetsObservable.subscribe({
        next: (targets: PatientTargetModel[]) => {
          this.supervisorHelperService.targets.set(targets);
          this.targetToOpen = targets.find((t) => t.ID === target.Id) ?? null;
          this.isLoading = false;
        },
        error: (error: any) => {
          this.isLoading = false;
        },
      });
    } else {
      this.targetToOpen =
        this.supervisorHelperService
          .targets()
          .find((t) => t.ID === target.Id) ?? null;
    }
  }
}
